import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Button, Modal } from '../../../../../components';
import api from '../../../../../utils/api';
import { copyToClipboard } from '../../../../../utils/copyToClipboard';

interface VerifyEndpointModalProps {
  isOpen: boolean;
  agentUserId: string;
  endpointId: string;
  endpointUrl: string;
  /**
   * Fires with the updated verification row after a successful
   * check so the parent can refresh its status badge without a
   * full refetch. Also called on modal close.
   */
  onClose: (updated?: VerificationRow) => void;
}

interface VerificationRow {
  id: string;
  type: 'email' | 'endpoint' | 'dns_txt';
  targetValue: string;
  status: 'pending' | 'verified' | 'stale' | 'revoked';
  verifiedAt: string | null;
}

interface StartResponse {
  verification: VerificationRow;
  token: string;
  wellKnownUrl: string;
  reissued: boolean;
  instructions: string;
}

/**
 * Owner flow for the ADR-012 endpoint verification handshake.
 *
 * Modal owns the two-step flow end-to-end so the edit page just
 * hosts a "Verify" button per endpoint card:
 *
 *   1. On open, POST /verify/endpoint → receive token + wellKnownUrl
 *   2. Show the owner what to host + where (copy-friendly, plain
 *      text or JSON envelope both accepted per PR C's contract)
 *   3. Owner deploys the static file; taps "Run check"
 *   4. POST /verify/endpoint/:id/check → surfaces success/failure
 *
 * Failure keeps the modal open + the token unchanged so the owner
 * can fix + retry without another initiate (matches the server's
 * "row stays pending on probe failure" semantics).
 */
export const VerifyEndpointModal: React.FC<VerifyEndpointModalProps> = ({
  isOpen,
  agentUserId,
  endpointId,
  endpointUrl,
  onClose,
}) => {
  const [initiateState, setInitiateState] = useState<
    'idle' | 'loading' | 'error' | 'ready'
  >('idle');
  const [start, setStart] = useState<StartResponse | null>(null);
  const [initiateError, setInitiateError] = useState<string | null>(null);

  const [checkState, setCheckState] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');
  const [checkError, setCheckError] = useState<string | null>(null);
  const [verified, setVerified] = useState<VerificationRow | null>(null);

  // Fire the initiate call as soon as the modal opens for the
  // given endpoint id; skip if we already have a token for this
  // exact endpoint (e.g. the modal was closed + reopened without
  // switching endpoints).
  useEffect(() => {
    if (!isOpen) return;
    if (start && start.verification.targetValue === endpointUrl) return;

    setInitiateState('loading');
    setInitiateError(null);
    setCheckState('idle');
    setCheckError(null);
    setVerified(null);

    api
      .post<StartResponse>(`/agents/mine/${agentUserId}/verify/endpoint`, {
        endpointId,
      })
      .then((res) => {
        setStart(res.data);
        setInitiateState('ready');
      })
      .catch((err) => {
        setInitiateError(
          err?.response?.data?.error || 'Failed to start verification',
        );
        setInitiateState('error');
      });
  }, [isOpen, agentUserId, endpointId, endpointUrl, start]);

  const handleCheck = async () => {
    if (!start) return;
    setCheckState('loading');
    setCheckError(null);
    try {
      const res = await api.post<{ verification: VerificationRow }>(
        `/agents/mine/${agentUserId}/verify/endpoint/${start.verification.id}/check`,
      );
      setVerified(res.data.verification);
      setCheckState('success');
      toast.success('Endpoint verified');
    } catch (err: any) {
      const reason =
        err?.response?.data?.reason ||
        err?.response?.data?.error ||
        'Check failed';
      setCheckError(reason);
      setCheckState('error');
    }
  };

  const handleClose = () => {
    // Reset the entire flow so the next open starts fresh.
    setInitiateState('idle');
    setStart(null);
    setInitiateError(null);
    setCheckState('idle');
    setCheckError(null);
    const updated = verified;
    setVerified(null);
    onClose(updated ?? undefined);
  };

  const copyToken = async () => {
    if (!start) return;
    if (await copyToClipboard(start.token)) toast.success('Token copied');
    else toast.error('Copy failed — select and copy manually');
  };

  const copyUrl = async () => {
    if (!start) return;
    if (await copyToClipboard(start.wellKnownUrl)) toast.success('URL copied');
    else toast.error('Copy failed');
  };

  return (
    <Modal
      isOpen={isOpen}
      title="Verify endpoint"
      onClose={handleClose}
      size="md"
      footer={
        <div className="verify-endpoint-modal__footer">
          <Button variant="secondary" onClick={handleClose}>
            {checkState === 'success' ? 'Done' : 'Close'}
          </Button>
          {initiateState === 'ready' && checkState !== 'success' && (
            <Button
              onClick={handleCheck}
              disabled={checkState === 'loading'}
            >
              {checkState === 'loading' ? 'Checking…' : 'Run check'}
            </Button>
          )}
        </div>
      }
    >
      <div className="verify-endpoint-modal">
        <p className="verify-endpoint-modal__endpoint-url">
          <span className="verify-endpoint-modal__label">Endpoint</span>
          <code>{endpointUrl}</code>
        </p>

        {initiateState === 'loading' && (
          <p className="verify-endpoint-modal__hint">Issuing challenge…</p>
        )}

        {initiateState === 'error' && (
          <p className="verify-endpoint-modal__error">
            {initiateError}
          </p>
        )}

        {initiateState === 'ready' && start && (
          <>
            <ol className="verify-endpoint-modal__steps">
              <li>
                <div>
                  Host this <strong>challenge token</strong> as the response
                  body of the URL below (plain text is fine; a JSON envelope{' '}
                  <code>{'{ "token": "…" }'}</code> is also accepted).
                </div>
                <div className="verify-endpoint-modal__code-row">
                  <code className="verify-endpoint-modal__code">
                    {start.token}
                  </code>
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={copyToken}
                  >
                    Copy
                  </Button>
                </div>
              </li>
              <li>
                <div>
                  At this <strong>well-known URL</strong> on your endpoint's
                  origin:
                </div>
                <div className="verify-endpoint-modal__code-row">
                  <code className="verify-endpoint-modal__code">
                    {start.wellKnownUrl}
                  </code>
                  <Button variant="secondary" size="small" onClick={copyUrl}>
                    Copy
                  </Button>
                </div>
              </li>
              <li>
                Click <strong>Run check</strong> below. Klyx will fetch that
                URL, compare the body against the token, and mark the endpoint
                verified.
              </li>
            </ol>

            {start.reissued && (
              <p className="verify-endpoint-modal__reissued">
                Note: this token replaces any previous challenge for this
                endpoint.
              </p>
            )}

            {checkState === 'error' && (
              <p className="verify-endpoint-modal__error">
                Check failed: {checkError}. Fix the well-known response and
                click Run check again — the token is unchanged.
              </p>
            )}

            {checkState === 'success' && verified && (
              <p className="verify-endpoint-modal__success">
                ✓ Verified{' '}
                {verified.verifiedAt
                  ? `at ${new Date(verified.verifiedAt).toLocaleString()}`
                  : ''}
              </p>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default VerifyEndpointModal;
